// src/components/pages/admin-dash/BulkCredits.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";
import ConfirmDialog from "../../ui/ConfirmDialog";

/**
 * NEW: Bulk credit grant.
 *
 * Lets an admin grant the same number of credits to many users at once
 * — useful for promotional campaigns, customer-service makegoods, or
 * launching a new feature. Has three selection modes:
 *
 *   1. By user IDs   — paste/type a list of IDs
 *   2. All users     — every active user in one shot
 *   3. By role       — all users of a given type (e.g. all ADMIN)
 *
 * Suggested backend contract (TODO):
 *   POST /admin/user/bulk-credit
 *   {
 *     userIds:   number[],           // either this, or `filter`
 *     filter:    { active?: "TRUE", type?: "USER" | "ADMIN" },
 *     amount:    number,             // credits to add (can be negative)
 *     note:      string,             // audit note, required
 *     operation: "ADD" | "SET",      // default ADD
 *   }
 *
 * Props:
 *   onShowToast(message, tone)
 */
const MODES = [
  { id: "ids",    label: "Specific user IDs", icon: "list" },
  { id: "all",    label: "All active users",  icon: "users" },
  { id: "role",   label: "By role",           icon: "shield-check" },
];

export default function BulkCredits({ onShowToast }) {
  const [mode, setMode]       = useState("ids");
  const [idsRaw, setIdsRaw]   = useState("");
  const [role, setRole]       = useState("USER");
  const [amount, setAmount]   = useState("");
  const [note, setNote]       = useState("");
  const [op, setOp]           = useState("ADD");
  const [loadingCount, setLoadingCount] = useState(false);
  const [targetCount, setTargetCount]   = useState(null);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [submitting, setSubmitting]     = useState(false);

  // Parse pasted IDs (commas / spaces / newlines all work)
  const parsedIds = useMemo(() => {
    return Array.from(
      new Set(
        idsRaw
          .split(/[\s,;]+/g)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => Number(s))
          .filter((n) => Number.isFinite(n) && n > 0)
      )
    );
  }, [idsRaw]);

  // Estimate target count by hitting /admin/user/list-all for non-ID modes.
  useEffect(() => {
    if (mode === "ids") {
      setTargetCount(parsedIds.length);
      return;
    }
    let cancelled = false;
    setLoadingCount(true);
    axiosInstance
      .get("/admin/user/list-all")
      .then((r) => {
        if (cancelled) return;
        const all = Array.isArray(r.data) ? r.data : [];
        if (mode === "all") {
          setTargetCount(all.filter((u) => u.active === "TRUE").length);
        } else if (mode === "role") {
          setTargetCount(
            all.filter((u) => u.active === "TRUE" && u.type === role).length
          );
        }
      })
      .catch(() => !cancelled && setTargetCount(null))
      .finally(() => !cancelled && setLoadingCount(false));
    return () => { cancelled = true; };
  }, [mode, role, parsedIds.length]);

  const amountNum = Number(amount);
  const amountValid = amount !== "" && Number.isFinite(amountNum);
  const noteValid = note.trim().length >= 4;
  const targetValid = targetCount && targetCount > 0;

  const formValid = amountValid && noteValid && targetValid;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!formValid) return;
    setConfirmOpen(true);
  };

  const doApply = async () => {
    setSubmitting(true);
    try {
      const body = {
        amount: amountNum,
        operation: op,
        note: note.trim(),
      };
      if (mode === "ids") {
        body.userIds = parsedIds;
      } else if (mode === "all") {
        body.filter = { active: "TRUE" };
      } else if (mode === "role") {
        body.filter = { active: "TRUE", type: role };
      }
      const res = await axiosInstance.post("/admin/user/bulk-credit", body);
      const affected = res?.data?.affected ?? targetCount;
      onShowToast?.(
        `Credits applied to ${affected} user${affected === 1 ? "" : "s"}.`,
        "success"
      );
      setConfirmOpen(false);
      setIdsRaw("");
      setAmount("");
      setNote("");
    } catch (err) {
      onShowToast?.(
        err?.response?.data?.message || "Bulk credit grant failed.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------- Display strings --------------------------- */
  const targetSummary = useMemo(() => {
    if (mode === "ids")  return `${parsedIds.length} user${parsedIds.length === 1 ? "" : "s"}`;
    if (mode === "all")  return targetCount != null ? `~${targetCount} active users` : "all active users";
    if (mode === "role") return targetCount != null
      ? `~${targetCount} ${role === "ADMIN" ? "admins" : "users"}`
      : `all active ${role === "ADMIN" ? "admins" : "users"}`;
    return "";
  }, [mode, parsedIds.length, targetCount, role]);

  const actionSummary = (() => {
    if (!amountValid) return "—";
    const abs = Math.abs(amountNum);
    if (op === "SET") return `Set credit balance to ${amountNum}`;
    if (amountNum >= 0) return `Add ${abs} credit${abs === 1 ? "" : "s"}`;
    return `Subtract ${abs} credit${abs === 1 ? "" : "s"}`;
  })();

  return (
    <div className="space-y-4 max-w-3xl">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Bulk credits
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Grant or adjust credits across many users in one operation.
          All changes are logged with your audit note.
        </p>
      </header>

      <form onSubmit={handleSubmit}>
        <Card className="space-y-4">
          {/* Target selection */}
          <div>
            <div className="lg-label mb-2">Who gets the credits?</div>
            <div className="flex flex-wrap gap-2">
              {MODES.map((m) => (
                <ModeChip
                  key={m.id}
                  active={mode === m.id}
                  onClick={() => setMode(m.id)}
                  icon={m.icon}
                  label={m.label}
                />
              ))}
            </div>
          </div>

          {mode === "ids" && (
            <div>
              <label
                className="block text-[12px] font-medium mb-1.5 tracking-wide"
                style={{ color: "var(--txt)" }}
              >
                User IDs
              </label>
              <textarea
                rows={3}
                value={idsRaw}
                onChange={(e) => setIdsRaw(e.target.value)}
                placeholder="Paste IDs separated by commas, spaces, or new lines"
                className="w-full px-3.5 py-[11px] text-[13px] outline-none rounded-2xl border resize-y tabular-nums"
                style={{
                  background: "var(--surf-2)",
                  color: "var(--txt)",
                  borderColor: "var(--border-2)",
                }}
              />
              <p className="mt-1.5 text-[11px]" style={{ color: "var(--txt-3)" }}>
                {parsedIds.length} unique ID{parsedIds.length === 1 ? "" : "s"} parsed.
              </p>
            </div>
          )}

          {mode === "role" && (
            <div>
              <label
                className="block text-[12px] font-medium mb-1.5 tracking-wide"
                style={{ color: "var(--txt)" }}
              >
                Role
              </label>
              <div className="flex gap-2">
                <ModeChip
                  active={role === "USER"}
                  onClick={() => setRole("USER")}
                  icon="user"
                  label="User"
                />
                <ModeChip
                  active={role === "ADMIN"}
                  onClick={() => setRole("ADMIN")}
                  icon="shield-check"
                  label="Admin"
                />
              </div>
            </div>
          )}

          {/* Amount + operation */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label
                className="block text-[12px] font-medium mb-1.5 tracking-wide"
                style={{ color: "var(--txt)" }}
              >
                Operation
              </label>
              <div className="flex gap-2">
                <ModeChip
                  active={op === "ADD"}
                  onClick={() => setOp("ADD")}
                  icon="plus"
                  label="Add / subtract"
                />
                <ModeChip
                  active={op === "SET"}
                  onClick={() => setOp("SET")}
                  icon="check"
                  label="Set exact value"
                />
              </div>
            </div>

            <Field
              label={op === "SET" ? "Set balance to" : "Credits to apply"}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={op === "SET" ? "e.g. 100" : "e.g. 10 (use -5 to deduct)"}
              hint={op === "ADD" ? "Negative numbers deduct credits." : undefined}
            />
          </div>

          {/* Note */}
          <div>
            <label
              className="block text-[12px] font-medium mb-1.5 tracking-wide"
              style={{ color: "var(--txt)" }}
            >
              Audit note <span style={{ color: "var(--gold)" }}>· required</span>
            </label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Holiday promotion, refund for outage, etc."
              className="w-full px-3.5 py-[11px] text-[13px] outline-none rounded-2xl border resize-y"
              style={{
                background: "var(--surf-2)",
                color: "var(--txt)",
                borderColor: "var(--border-2)",
              }}
            />
            {note && note.trim().length < 4 && (
              <p className="mt-1.5 text-[11px]" style={{ color: "var(--red)" }}>
                Add at least a few words.
              </p>
            )}
          </div>

          {/* Summary */}
          <div
            className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
            style={{
              background: "var(--gold-dim)",
              border: "1px solid var(--gold-border)",
            }}
          >
            <div
              className="shrink-0 inline-flex items-center justify-center rounded-xl"
              style={{
                width: 36, height: 36,
                background: "var(--surf)",
                color: "var(--gold)",
                border: "1px solid var(--gold-border)",
              }}
            >
              <Icon name="coin" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                You're about to
              </div>
              <div className="font-medium" style={{ color: "var(--txt)" }}>
                {actionSummary} for{" "}
                {loadingCount && mode !== "ids" ? (
                  <span className="inline-flex items-center gap-1">
                    <Spinner size={11} /> loading…
                  </span>
                ) : (
                  <Badge variant="gold">{targetSummary}</Badge>
                )}
              </div>
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button icon="coin"
            size="sm"
            type="submit"
            disabled={!formValid || submitting}
            loading={submitting}
          >Apply credits
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmOpen}
        title="Apply credits to users?"
        message={`${actionSummary} for ${targetSummary}. This change is permanent and will be logged.`}
        confirmLabel="Apply"
        tone={amountNum < 0 || op === "SET" ? "danger" : "default"}
        loading={submitting}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doApply}
        icon="coin"
      />
    </div>
  );
}

function ModeChip({ active, onClick, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors"
      style={{
        background: active ? "var(--gold-dim)" : "var(--surf-2)",
        color:      active ? "var(--gold)"     : "var(--txt-2)",
        border: `1px solid ${active ? "var(--gold-border)" : "var(--border-2)"}`,
      }}
    >
      <Icon name={icon} size={12} />
      {label}
    </button>
  );
}
